import { shouldReloadForNewAdminBuild } from 'handlers/shouldReloadForNewAdminBuild'

/** Как часто проверять обновление SW и version.json */
const UPDATE_CHECK_INTERVAL_MS = 60_000

const KNOWN_BUILD_ID_KEY = 'admin-pwa-known-build-id'

type AdminVersionPayload = {
  buildId?: string
  builtAt?: string
}

/**
 * Регистрирует service worker и включает автообновление при деплое новой админки.
 * @returns registration или null
 */
export const registerAdminServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js')
    setupServiceWorkerUpdateHandlers(registration)
    void checkRemoteAdminVersionAndReload()
    window.setInterval(() => {
      void registration.update()
      void checkRemoteAdminVersionAndReload()
    }, UPDATE_CHECK_INTERVAL_MS)

    // Проверка при возврате на вкладку
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void registration.update()
        void checkRemoteAdminVersionAndReload()
      }
    })

    return registration
  } catch (error) {
    console.error('Ошибка регистрации service worker:', error)
    return null
  }
}

/**
 * Когда найден новый SW — активируем и перезагружаем страницу.
 * @param registration — результат register()
 */
const setupServiceWorkerUpdateHandlers = (
  registration: ServiceWorkerRegistration
): void => {
  let isRefreshing = false

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isRefreshing) {
      return
    }
    isRefreshing = true
    window.location.reload()
  })

  /**
   * Просит waiting-worker немедленно стать active.
   * @param worker — устанавливаемый SW
   */
  const activateWaitingWorker = (worker: ServiceWorker | null): void => {
    if (!worker) {
      return
    }
    worker.postMessage({ type: 'SKIP_WAITING' })
  }

  if (registration.waiting) {
    activateWaitingWorker(registration.waiting)
  }

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing
    if (!installingWorker) {
      return
    }
    installingWorker.addEventListener('statechange', () => {
      if (
        installingWorker.state === 'installed' &&
        navigator.serviceWorker.controller
      ) {
        activateWaitingWorker(installingWorker)
      }
    })
  })
}

/**
 * Сверяет /version.json с запомненным buildId; при отличии — reload.
 */
const checkRemoteAdminVersionAndReload = async (): Promise<void> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      return
    }
    const payload = (await response.json()) as AdminVersionPayload
    const remoteBuildId = payload?.buildId
    if (!remoteBuildId) {
      return
    }

    const knownBuildId = sessionStorage.getItem(KNOWN_BUILD_ID_KEY)
    if (!knownBuildId) {
      sessionStorage.setItem(KNOWN_BUILD_ID_KEY, remoteBuildId)
      return
    }

    if (shouldReloadForNewAdminBuild(knownBuildId, remoteBuildId)) {
      sessionStorage.setItem(KNOWN_BUILD_ID_KEY, remoteBuildId)
      window.location.reload()
    }
  } catch (_) {
    // version.json может отсутствовать в dev — не шумим
  }
}
